import type React from 'react';
import { useState, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useNavigate, generatePath } from 'react-router-dom';

import Button from 'app/ui/components/atoms/button';
import { routes } from 'app/routes/constants';
import { trackEvent } from 'src/app/utils/analytics/tracker';

import { StartProgressivePlan as START_PROGRESSIVE_PLAN } from 'app/pages/progressive-plan/mutation.gql';
import type { StartProgressivePlanMutation, StartProgressivePlanMutationVariables } from 'app/types/graphql';
import { PLANS_CAROUSEL_STORAGE_KEY, DEFAULT_MAX_SHOW_TIMES } from 'app/ui/components/molecules/carousel-modal';

type StartPlanEventName = 'start_plan_cta_clicked' | 'start_plan_banner_clicked';

interface UseStartProgressivePlanOptions {
  planId: string;
  planTitle?: string;
  trackingEventName?: StartPlanEventName;
}

interface DialogConfig {
  primaryText: string;
  primaryButton?: React.ReactNode;
}

interface UseStartProgressivePlanReturn {
  handleStartPlanClick: () => void;
  handleStartPlanConfirm: () => void;
  isCarouselModalOpen: boolean;
  setIsCarouselModalOpen: (isOpen: boolean) => void;
  dialogConfig: DialogConfig | null;
  setDialogConfig: (config: DialogConfig | null) => void;
  isLoading: boolean;
  error: Error | null;
}

export const useStartProgressivePlan = ({
  planId,
  planTitle = '',
  trackingEventName = 'start_plan_cta_clicked',
}: UseStartProgressivePlanOptions): UseStartProgressivePlanReturn => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [isCarouselModalOpen, setIsCarouselModalOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<DialogConfig | null>(null);

  // Track whether user went through the carousel (to skip dialog if they did)
  const showedCarouselRef = useRef(false);

  const [startPlan, { loading: isLoading, error }] = useMutation<
    StartProgressivePlanMutation,
    StartProgressivePlanMutationVariables
  >(START_PROGRESSIVE_PLAN, {
    refetchQueries: ['ProgressivePlan'],
    update: (cache) => {
      cache.evict({ fieldName: 'allProgressivePlanInstances' });
      cache.evict({ fieldName: 'allProgressivePlans' });
      cache.gc();
    },
    onCompleted: (data) => {
      if (data?.startProgressivePlan?.id) {
        const instanceId = data.startProgressivePlan.id;

        if (showedCarouselRef.current) {
          navigate(generatePath(routes.TRAINING_PLAN_ACTIVE_INSTANCE, { id: instanceId }));
          showedCarouselRef.current = false;
        } else {
          setDialogConfig({
            primaryText: intl.formatMessage({
              id: 'dialog.trainingPlanStartedPrimary',
              defaultMessage: 'Training plan started!',
            }),
            primaryButton: (
              <Button
                label={intl.formatMessage({ id: 'dialog.close', defaultMessage: 'Close' })}
                onClick={() => {
                  navigate(generatePath(routes.TRAINING_PLAN_ACTIVE_INSTANCE, { id: instanceId }));
                  setDialogConfig(null);
                }}
              />
            ),
          });
        }
      }
    },
    onError: (err) => {
      console.error('Error starting plan:', err);
      showedCarouselRef.current = false;
      setDialogConfig({
        primaryText: intl.formatMessage({
          id: 'dialog.trainingPlanStartError',
        }),
        primaryButton: (
          <Button
            label={intl.formatMessage({ id: 'dialog.close', defaultMessage: 'Close' })}
            onClick={() => setDialogConfig(null)}
          />
        ),
      });
    },
  });

  const handleStartPlanConfirm = useCallback(() => {
    if (planId) {
      startPlan({ variables: { input: { progressivePlanId: planId } } });
    }
  }, [planId, startPlan]);

  const handleStartPlanClick = useCallback(() => {
    if (!planId) {
      return;
    }

    trackEvent(trackingEventName, {
      plan_id: planId,
      plan_title: planTitle,
    });

    // Check if user has already seen the onboarding carousel
    const carouselShownTimes = Number(localStorage.getItem(PLANS_CAROUSEL_STORAGE_KEY)) || 0;
    if (carouselShownTimes >= DEFAULT_MAX_SHOW_TIMES) {
      showedCarouselRef.current = false;
      handleStartPlanConfirm();
    } else {
      showedCarouselRef.current = true;
      setIsCarouselModalOpen(true);
    }
  }, [planId, planTitle, trackingEventName, handleStartPlanConfirm]);

  return {
    handleStartPlanClick,
    handleStartPlanConfirm,
    isCarouselModalOpen,
    setIsCarouselModalOpen,
    dialogConfig,
    setDialogConfig,
    isLoading,
    error: error || null,
  };
};
